// src/pages/WhatsTarot.js
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/seo";
import { isSpanishLocale, localizedPath, translateSuitName } from "../utils/i18n";

/* =========================================================
   THEME TOKENS
========================================================= */
const WRAP = "px-4 sm:px-6";
const MAXW = "max-w-6xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/84";
const PAGE_BG = `
  radial-gradient(900px 560px at 70% 0%, rgba(156,231,238,0.14), transparent 58%),
  linear-gradient(180deg, #014C75 0%, #005C83 30%, #0C4B84 74%, #821E3C 120%)
`;
const GUIDE_SHELL =
  "overflow-hidden rounded-lg border border-[#9ce7ee]/24 bg-[linear-gradient(180deg,rgba(4,47,76,0.95),rgba(3,52,79,0.93)_48%,rgba(18,44,76,0.94)_100%)] text-white shadow-[0_22px_60px_rgba(2,10,26,0.24)]";
const SECTION =
  "relative px-4 py-8 text-left sm:px-6 sm:py-10 lg:grid lg:grid-cols-[minmax(250px,0.72fr)_minmax(0,1.55fr)] lg:gap-12";
const SECTION_DIVIDER =
  "pointer-events-none absolute inset-x-0 top-0 h-px bg-[#cbb7ea]/60";
const TITLE =
  "text-base font-semibold uppercase tracking-[0.12em] text-white/95 sm:text-lg";
const SUBTITLE = `${TEXT} text-sm leading-relaxed`;
const BODY = `${TEXT} text-base leading-relaxed`;
const LINK_BUTTON =
  "inline-flex min-h-11 items-center justify-center rounded-md border border-[#9ce7ee]/30 bg-[#014c75]/70 px-4 py-2 text-center text-[14px] font-bold uppercase tracking-[0.08em] text-white/90 transition hover:border-[#f2d38a]/70 hover:bg-[#075f86] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9ce7ee]/80";

/* =========================================================
   SEO
========================================================= */
const siteUrl = "https://tarotfate.com";
const pageUrl = `${siteUrl}/what-is-tarot-cards-complete-guide`;
const logoUrl = `${siteUrl}/icons/logo.webp`;
const ogImage = `${siteUrl}/icons/og-cover.jpg`;

const metaDescription =
  "The complete tarot guide. Learn what tarot is, how readings work, deck structure, suits and elements, numerology, court cards, spreads, ethics, reversals and a step by step example. Clear guidance for love, career and personal growth.";

const megaKeywords = `
  what is tarot, tarot guide, tarot for beginners, how tarot works, tarot meanings, major arcana, minor arcana,
  wands cups swords pentacles, suits and elements, tarot numerology, court cards explained, tarot spreads,
  three card spread, celtic cross, daily card, asking better tarot questions, tarot reversals, significators,
  clarifiers, tarot ethics, consent, tarot journal, cleansing a tarot deck, choosing a tarot deck,
  upright meanings, reversed meanings, symbolism in tarot, history of tarot, self reflection, spiritual growth,
  love readings, career readings, personal development, meditation with tarot, mindfulness with tarot
`.replace(/\s+/g, " ").trim();

/* =========================================================
   Copy
========================================================= */
const heroLead =
  "Tarot is a mirror that reflects pattern and choice. This guide explains what tarot is, how a reading works, why the images speak with clarity and how to begin with confidence whether you are reading for yourself or for others.";

const deckOverview =
  "A complete tarot deck contains seventy eight cards. The Major Arcana includes twenty two archetypes that describe the larger journey of growth and awakening. The Minor Arcana contains fifty six cards across the suits of Wands, Cups, Swords and Pentacles. These suits describe real situations and practical decisions across energy and ambition, emotion and relationship, thought and communication and work and resources.";

const howItWorksIntro =
  "A tarot reading is a focused conversation. You bring a sincere question. The spread arranges images that help you hear your own wisdom with precision. The process is reflective and practical and it supports action that aligns with your values.";

const ethicsBlock =
  "Tarot supports reflection and growth. It does not replace medical, legal or financial guidance. Read with informed consent and protect privacy. Avoid determinism and invite agency. Encourage choices that honor health, safety and core values.";

const chooseIntro =
  "Choose a deck that you enjoy looking at and that you will use often. Preference matters more than trend. Hold the cards, notice how the images make you feel and choose a size that fits your hands.";

const careIntro =
  "Treat the deck with respect. Keep it dry and clean. Reset your attention before each reading. Some readers mark intention with light, sound or time in nature. The method matters less than sincere attention.";

const elementsIntro =
  "The four suits map to the four classical elements. This framework helps you read across the deck with consistency and depth.";

const numerologyIntro =
  "Numbers add a steady layer of meaning. The sequence from Ace through Ten describes a full cycle from beginning to integration.";

const courtsIntro =
  "The court cards model roles and styles. They can describe you, another person, a way of acting or a quality to cultivate in the moment.";

const questionsIntro =
  "A clear question produces a clear reading. Use language that invites awareness and choice. Avoid wording that removes agency or seeks control over others.";

const reversalsIntro =
  "Reversed cards are a reader choice. They can signal a block, a private process or a reduced expression of the upright meaning. Use them if they help you see nuance.";

const exampleIntro =
  "This example shows a simple three card process for a common situation. Use it as a model and adjust to your needs.";

const faqList = [
  { q: "What is tarot used for?", a: "Tarot is used for reflection, clarity and decision support. It helps you name what matters, understand patterns and choose the next step that aligns with your values." },
  { q: "Is tarot fortune telling?", a: "Tarot does not fix your future. It shows influences and options. Your choices shape outcome. The value of a reading is insight and aligned action." },
  { q: "Can I read for myself?", a: "Yes. Read for yourself with honesty and patience. If emotions are intense, ask a trusted reader to reflect with you or take a break and return when you feel steady." },
  { q: "Do I need to use reversed meanings?", a: "Use reversals if they add helpful nuance. If you prefer, read upright meanings and rely on position and context. Choose one approach and apply it with consistency." },
  { q: "How accurate are tarot readings?", a: "Accuracy improves with a clear question, an ethical process and consistent practice. Keep a journal and compare impressions with results over time." },
  { q: "How do I ask a good tarot question?", a: "Ask open questions that you can act on. For example ask what supports progress or what choice aligns with your values. Avoid questions that control others." },
  { q: "What is a significator in tarot?", a: "A significator is a card chosen to represent the seeker or the focus of a reading. You can select it by court rank, zodiac or intuitive fit. Using a significator is optional." },
  { q: "What is a clarifier card?", a: "A clarifier is an additional draw used when a position needs more detail. Use clarifiers with restraint and only after you have explored the original card fully." },
];

const quickLinks = [
  ["#deck-structure", "Deck"],
  ["#how-it-works", "Reading"],
  ["#spreads", "Spreads"],
  ["#choose", "Deck choice"],
  ["#care", "Care"],
  ["#elements", "Suits"],
  ["#numerology", "Numbers"],
  ["#courts", "Courts"],
  ["#questions", "Questions"],
  ["#reversals", "Reversals"],
  ["#example", "Example"],
  ["#myths", "Myths"],
  ["#faq", "FAQ"],
];

const deckLinks = [
  ["/tarot/major-arcana", "Major Arcana"],
  ["/tarot/wands", "Wands"],
  ["/tarot/cups", "Cups"],
  ["/tarot/swords", "Swords"],
  ["/tarot/pentacles", "Pentacles"],
  ["/tarot-cards", "All Cards"],
];

const readingSteps = [
  ["Set intention", "Name a sincere and actionable focus. Breathe and state your aim for clarity and care."],
  ["Shuffle and draw", "Draw one card for a daily focus or three cards for Past, Present and Future."],
  ["Describe the image", "Note symbols, posture, color and your first feeling before checking any guide."],
  ["Integrate meaning", "Combine impressions with the role of the position to form a clear message."],
  ["Choose one next step", "Translate guidance into a practical action that you will take within a realistic time frame."],
  ["Journal and review", "Record result and return weekly to refine your understanding and accuracy."],
];

const spreads = [
  ["Single card daily pull", "Ask what supports you today. Draw one card and name one action you will take before the day ends."],
  ["Past, Present and Future", "Three cards show context, current focus and likely direction. Use the message to choose the next step."],
  ["Choice A and B", "Two cards outline each option. A third card offers guidance that helps you decide with integrity."],
];

const suitMeanings = [
  ["Wands / Fire", "Energy, initiative, leadership, creativity, visibility and movement."],
  ["Cups / Water", "Emotion, relationship, intuition, empathy, healing and imagination."],
  ["Swords / Air", "Thought, clarity, truth, language, analysis, choice and ethics."],
  ["Pentacles / Earth", "Body, work, money, craft, skill, home, time and steady growth."],
];

const numberMeanings = [
  ["Ace", "Source, spark and pure potential."],
  ["Two", "Choice, balance, polarity and partnership."],
  ["Three", "Growth, synthesis and early result."],
  ["Four", "Stability, structure, rest and review."],
  ["Five", "Change, challenge and a test of values."],
  ["Six", "Flow, harmony, recovery and support."],
  ["Seven", "Assessment, strategy and patience."],
  ["Eight", "Skill, power, practice and movement."],
  ["Nine", "Attainment, refinement and near completion."],
  ["Ten", "Culmination, transition and the handoff to a new cycle."],
];

const courtMeanings = [
  ["Page", "Student energy, curiosity, message and beginning a skill."],
  ["Knight", "Movement, mission, pursuit and momentum."],
  ["Queen", "Mastery with empathy, inner authority and stewardship."],
  ["King", "Structure, vision, accountability and public leadership."],
];

const myths = [
  ["You must be given your first deck.", "You may buy your own deck. Choose one that you like and will use."],
  ["Tarot fixes the future.", "Tarot describes influences and options. Choice and action shape the result."],
  ["Only gifted psychics can read.", "Anyone can learn. Practice, reflection and ethics build skill and trust."],
  ["Reversals are mandatory.", "Reversals are optional. Consistency in your method matters most."],
];

/* =========================================================
   Smooth offset scroll helper
========================================================= */
function scrollWithOffset(hash, smooth = true) {
  if (!hash) return;
  const el = document.querySelector(hash);
  if (!el) return;
  const navH = document.getElementById("site-nav")?.offsetHeight || 64;
  const extra = 8;
  const y = window.scrollY + el.getBoundingClientRect().top - navH - extra;
  const opts = smooth ? { top: Math.max(0, y), behavior: "smooth" } : { top: Math.max(0, y) };
  window.scrollTo(opts);
}

function GuideLink({ href, children }) {
  if (href.startsWith("#")) {
    return (
      <a
        href={href}
        onClick={(e) => {
          e.preventDefault();
          window.history.replaceState(null, "", href);
          scrollWithOffset(href, true);
        }}
        className={LINK_BUTTON}
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={LINK_BUTTON}>
      {children}
    </Link>
  );
}

function GuideSection({ id, title, intro, children }) {
  return (
    <section id={id} className={SECTION}>
      <div aria-hidden="true" className={SECTION_DIVIDER} />
      <div className="mb-5 lg:mb-0">
        <h2 className={TITLE}>{title}</h2>
        {intro && <p className={`${SUBTITLE} mt-2 max-w-[38rem]`}>{intro}</p>}
      </div>
      <div className="min-w-0">{children}</div>
    </section>
  );
}

function DefinitionList({ items }) {
  return (
    <dl className="space-y-5">
      {items.map(([term, definition], index) => (
        <div
          key={term}
          className={`grid gap-1 sm:grid-cols-[170px_minmax(0,1fr)] sm:gap-6 ${
            index > 0
              ? "relative pt-5 before:absolute before:left-0 before:right-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-[#cbb7ea]/45 before:to-transparent"
              : ""
          }`}
        >
          <dt className="text-base font-semibold leading-snug text-white/95">{term}</dt>
          <dd className={`${TEXT} text-sm leading-relaxed sm:text-base`}>{definition}</dd>
        </div>
      ))}
    </dl>
  );
}

function StepList({ items }) {
  return (
    <div className="space-y-5">
      {items.map(([title, text], index) => (
        <section
          key={title}
          className={
            index > 0
              ? "relative pt-5 before:absolute before:left-0 before:right-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-[#cbb7ea]/45 before:to-transparent"
              : ""
          }
        >
          <div>
            <strong className="block text-base font-semibold leading-snug text-white/95">{title}</strong>
            <span className={`${TEXT} mt-1 block text-sm leading-relaxed sm:text-base`}>{text}</span>
          </div>
        </section>
      ))}
    </div>
  );
}

function CompactMeaningMap({ items }) {
  return (
    <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
      {items.map(([term, definition]) => (
        <div key={term} className="grid min-w-0 grid-cols-[4.25rem_minmax(0,1fr)] gap-3">
          <strong className="flex h-9 items-center justify-center rounded-sm bg-white/[0.06] text-sm font-semibold uppercase tracking-[0.08em] text-[#F7E4B0] ring-1 ring-white/10">
            {term}
          </strong>
          <span className={`${TEXT} min-w-0 text-sm leading-relaxed sm:text-base`}>
            {definition}
          </span>
        </div>
      ))}
    </div>
  );
}

function SoftList({ items }) {
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className={`${TEXT} relative pl-5 text-sm leading-relaxed before:absolute before:left-0 before:top-[0.72em] before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#f2d38a]/80 sm:text-base`}>
          {item}
        </li>
      ))}
    </ul>
  );
}

function SplitList({ leftTitle, leftItems, rightTitle, rightItems }) {
  return (
    <div className="grid gap-8 md:grid-cols-2">
      <section>
        <h3 className="text-base font-semibold uppercase tracking-[0.12em] text-white/95 sm:text-lg">{leftTitle}</h3>
        <div className="mt-3">
          <SoftList items={leftItems} />
        </div>
      </section>
      <section>
        <h3 className="text-base font-semibold uppercase tracking-[0.12em] text-white/95 sm:text-lg">{rightTitle}</h3>
        <div className="mt-3">
          <SoftList items={rightItems} />
        </div>
      </section>
    </div>
  );
}

/* =========================================================
   JSON-LD
========================================================= */
function buildSchemaGraph() {
  const faqEntities = faqList.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  }));

  const howToDaily = {
    "@type": "HowTo",
    name: "How to do a daily one card tarot reading",
    description: "A simple daily practice that builds clarity and trust.",
    step: [
      { "@type": "HowToStep", name: "Form a question", text: "Ask what supports you today. Name your intention for clarity and care." },
      { "@type": "HowToStep", name: "Shuffle and draw", text: "Shuffle with attention. Draw one card." },
      { "@type": "HowToStep", name: "Describe what you see", text: "Note symbols, posture, color and your first feeling." },
      { "@type": "HowToStep", name: "Choose one action", text: "Translate the card into one practical step you will take today." },
      { "@type": "HowToStep", name: "Record and review", text: "Write the result in a journal and review progress each week." },
    ],
  };

  const howToThreeCard = {
    "@type": "HowTo",
    name: "How to do a three card tarot reading",
    description: "A clear structure for Past, Present and Future.",
    step: [
      { "@type": "HowToStep", name: "Set your focus", text: "Name the situation. Ask what supports wise choice now." },
      { "@type": "HowToStep", name: "Lay three cards", text: "Place cards left to right for Past, Present and Future." },
      { "@type": "HowToStep", name: "Interpret positions", text: "Past gives context. Present names the core. Future shows likely direction." },
      { "@type": "HowToStep", name: "Synthesize", text: "Combine images and meanings to form one sentence of guidance." },
      { "@type": "HowToStep", name: "Act and reflect", text: "Choose a next step and record outcome to refine your process." },
    ],
  };

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: { "@type": "ImageObject", url: logoUrl, width: 512, height: 512 },
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: "en",
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "What is Tarot? Complete Guide | TarotFate",
        description: metaDescription,
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: { "@id": `${pageUrl}#image` },
        breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
        inLanguage: "en",
      },
      {
        "@type": "ImageObject",
        "@id": `${pageUrl}#image`,
        url: ogImage,
        width: 1200,
        height: 630,
        caption: "TarotFate cover",
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
          { "@type": "ListItem", position: 2, name: "What is Tarot", item: pageUrl },
        ],
      },
      { "@type": "FAQPage", "@id": `${pageUrl}#faq`, mainEntity: faqEntities },
      { "@type": "HowTo", "@id": `${pageUrl}#howto-daily`, ...howToDaily },
      { "@type": "HowTo", "@id": `${pageUrl}#howto-three-card`, ...howToThreeCard },
    ],
  };
}

export default function WhatsTarot() {
  const isSpanish = isSpanishLocale();
  const copy = isSpanish
    ? {
        title: "¿Qué es el tarot?",
        seoTitle: "¿Qué es el tarot? Guía completa | TarotFate",
        description:
          "Guía completa de tarot en español. Aprende qué es el tarot, cómo funciona una lectura, estructura del mazo, palos, números, cartas de la corte, preguntas, invertidas y práctica ética.",
        hero:
          "El tarot es un espejo que ayuda a reconocer patrones, emociones y decisiones. Esta guía explica qué es el tarot, cómo funciona una lectura y cómo empezar con confianza sin perder sensibilidad ni claridad.",
        glance: "El mazo en breve",
        labels: ["Cartas", "Mayores", "Menores"],
        quickLinks: [
          ["#deck-structure", "Mazo"],
          ["#how-it-works", "Lectura"],
          ["#spreads", "Tiradas"],
          ["#choose", "Elegir"],
          ["#care", "Cuidado"],
          ["#elements", "Palos"],
          ["#numerology", "Números"],
          ["#courts", "Corte"],
          ["#questions", "Preguntas"],
          ["#reversals", "Invertidas"],
          ["#example", "Ejemplo"],
          ["#myths", "Mitos"],
          ["#faq", "FAQ"],
        ],
        deckOverview:
          "Un mazo completo contiene setenta y ocho cartas. Los Arcanos Mayores muestran veintidós arquetipos del viaje interior. Los Arcanos Menores reúnen cincuenta y seis cartas en Bastos, Copas, Espadas y Oros, que hablan de energía, vínculos, pensamiento, trabajo y recursos.",
        howItWorks:
          "Una lectura de tarot es una conversación enfocada. Traes una pregunta sincera y la tirada organiza imágenes que ayudan a escuchar tu propia sabiduría con más precisión.",
        spreadsIntro: "Empieza pequeño y construye confianza. Estas tiradas son claras, flexibles y fáciles de repetir.",
        choose: "Elige un mazo que te guste mirar y que quieras usar con frecuencia. La conexión personal importa más que la tendencia.",
        care: "Cuida el mazo con respeto. Mantenlo seco y limpio, y vuelve a tu intención antes de cada lectura.",
        elements: "Los cuatro palos se relacionan con los elementos clásicos y ayudan a leer el mazo con consistencia.",
        numbers: "Los números agregan una capa estable de significado. Del As al Diez aparece un ciclo completo.",
        courts: "Las cartas de la corte muestran roles, estilos y formas de actuar que puedes reconocer en ti o en otras personas.",
        questions:
          "Una pregunta clara produce una lectura más clara. Usa lenguaje que invite conciencia, elección y responsabilidad.",
        reversals:
          "Las cartas invertidas son una elección del lector. Pueden mostrar bloqueo, proceso interno o una expresión reducida del significado al derecho.",
        example:
          "Este ejemplo muestra una lectura sencilla de tres cartas para una situación común. Úsalo como modelo y ajústalo a tus necesidades.",
        ethics:
          "El tarot apoya reflexión y crecimiento. No reemplaza orientación médica, legal o financiera. Lee con consentimiento, privacidad y respeto por la capacidad de elegir.",
        deckItems: [
          ["Arcanos Mayores", "Veintidós cartas arquetípicas que describen el viaje espiritual profundo."],
          ["Arcanos Menores", "Cincuenta y seis cartas que llevan el tarot a decisiones y experiencias cotidianas."],
          ["Cuatro palos", "Bastos, Copas, Espadas y Oros muestran distintos tipos de experiencia humana."],
        ],
        deckLinks: deckLinks.map(([href, label]) => [
          localizedPath(href),
          label === "All Cards" ? "Todas las cartas" : translateSuitName(label),
        ]),
        readingSteps: [
          ["Define la intención", "Nombra una pregunta sincera y accionable. Respira y pide claridad con cuidado."],
          ["Baraja y roba", "Saca una carta para enfoque diario o tres cartas para pasado, presente y dirección."],
          ["Describe la imagen", "Observa símbolos, postura, color y tu primera sensación antes de mirar una guía."],
          ["Integra el significado", "Une tu impresión con la posición de la carta para formar un mensaje claro."],
          ["Elige un paso", "Convierte la guía en una acción realista que puedas tomar pronto."],
          ["Escribe y revisa", "Anota resultados y vuelve cada semana para afinar tu lectura."],
        ],
        spreads: [
          ["Una carta diaria", "Pregunta qué te sostiene hoy. Roba una carta y nombra una acción concreta."],
          ["Pasado, presente y futuro", "Tres cartas muestran contexto, foco actual y dirección probable."],
          ["Opción A y B", "Dos cartas muestran cada opción. Una tercera ofrece guía para decidir con integridad."],
        ],
        suitMeanings: [
          ["Bastos / Fuego", "Energía, iniciativa, liderazgo, creatividad, visibilidad y movimiento."],
          ["Copas / Agua", "Emoción, relación, intuición, empatía, sanación e imaginación."],
          ["Espadas / Aire", "Pensamiento, claridad, verdad, lenguaje, análisis, elección y ética."],
          ["Oros / Tierra", "Cuerpo, trabajo, dinero, oficio, hogar, tiempo y crecimiento estable."],
        ],
        numberMeanings: [
          ["As", "Fuente, chispa y potencial puro."],
          ["Dos", "Elección, equilibrio, polaridad y vínculo."],
          ["Tres", "Crecimiento, síntesis y primer resultado."],
          ["Cuatro", "Estabilidad, estructura, descanso y revisión."],
          ["Cinco", "Cambio, desafío y prueba de valores."],
          ["Seis", "Flujo, armonía, recuperación y apoyo."],
          ["Siete", "Evaluación, estrategia y paciencia."],
          ["Ocho", "Habilidad, práctica, poder y movimiento."],
          ["Nueve", "Logro, refinamiento y casi cierre."],
          ["Diez", "Culminación, transición y comienzo de un nuevo ciclo."],
        ],
        courtMeanings: [
          ["Sota", "Energía de estudiante, curiosidad, mensaje y comienzo de una habilidad."],
          ["Caballero", "Movimiento, misión, búsqueda y momentum."],
          ["Reina", "Maestría con empatía, autoridad interior y cuidado."],
          ["Rey", "Estructura, visión, responsabilidad y liderazgo visible."],
        ],
        helpful: ["Formas útiles", "Evitar"],
        helpfulItems: [
          "¿Qué apoya el avance en esta situación?",
          "¿Qué elección está alineada con mis valores?",
          "¿Qué puedo aprender de este conflicto?",
          "¿Cuál es el siguiente paso sabio que puedo tomar?",
        ],
        avoidItems: [
          "Preguntas que intentan controlar decisiones de otra persona.",
          "Preguntas de sí o no que bloquean la reflexión.",
          "Preguntas deterministas que eliminan tu agencia.",
          "Preguntas hechas en un estado de daño o riesgo.",
        ],
        reversalsItems: [
          ["Trabajar con invertidas", "Decide un método. Léelas como bloqueo, proceso privado o expresión reducida. Mantén consistencia."],
          ["Cuándo sacar aclaratoria", "Saca una sola aclaratoria después de trabajar la carta original. Pregunta algo específico y detente ahí."],
          ["Significadores", "Elige una carta para representar a la persona o el tema si te ayuda a enfocar."],
        ],
        exampleItems: [
          ["Pregunta", "¿Qué apoya una elección sabia sobre un nuevo rol en el trabajo?"],
          ["Tirada", "Tres cartas para pasado, presente y futuro."],
          ["Cartas", "Pasado: Tres de Oros. Presente: Dos de Bastos. Futuro: El Carro."],
        ],
        exampleText:
          "El pasado muestra colaboración y habilidad en crecimiento. El presente muestra visión y necesidad de elegir dirección. El futuro muestra movimiento enfocado y compromiso.",
        mythsIntro: "Muchos mitos rodean al tarot. Aclararlos ayuda a usar el mazo con confianza y respeto.",
        myths: [
          ["Tu primer mazo debe ser regalado.", "Puedes comprar tu propio mazo. Elige uno que te guste y vayas a usar."],
          ["El tarot fija el futuro.", "El tarot muestra influencias y opciones. Tus decisiones también forman el resultado."],
          ["Solo personas psíquicas pueden leer.", "Cualquier persona puede aprender con práctica, reflexión y ética."],
          ["Las invertidas son obligatorias.", "Son opcionales. Lo importante es la consistencia del método."],
        ],
        faq: [
          { q: "¿Para qué se usa el tarot?", a: "Para reflexión, claridad y apoyo en decisiones. Ayuda a nombrar lo importante y elegir un siguiente paso." },
          { q: "¿El tarot predice el futuro?", a: "No fija tu futuro. Muestra influencias, patrones y opciones. Tus acciones importan." },
          { q: "¿Puedo leer para mí?", a: "Sí. Lee con honestidad y paciencia. Si la emoción es muy intensa, pausa y vuelve cuando estés más estable." },
          { q: "¿Necesito leer invertidas?", a: "Úsalas si aportan matiz. También puedes leer al derecho y apoyarte en la posición y la pregunta." },
          { q: "¿Cómo hago una buena pregunta?", a: "Pregunta de forma abierta y accionable. Busca dirección, apoyo o el siguiente paso." },
        ],
      }
    : {
        title: "What Is Tarot?",
        seoTitle: "What is Tarot? Complete Guide | TarotFate",
        description: metaDescription,
        hero: heroLead,
        glance: "Deck at a glance",
        labels: ["Cards", "Major", "Minor"],
        quickLinks,
        deckOverview,
        howItWorks: howItWorksIntro,
        spreadsIntro: "Start small and build trust in your process. These spreads are clear and flexible.",
        choose: chooseIntro,
        care: careIntro,
        elements: elementsIntro,
        numbers: numerologyIntro,
        courts: courtsIntro,
        questions: questionsIntro,
        reversals: reversalsIntro,
        example: exampleIntro,
        ethics: ethicsBlock,
        deckItems: [
          ["Major Arcana", "Twenty two archetypal cards that describe the larger spiritual journey."],
          ["Minor Arcana", "Fifty six cards that bring tarot into daily choices, timing and practical life."],
          ["Four suits", "Wands, Cups, Swords and Pentacles each hold a different kind of human experience."],
        ],
        deckLinks,
        readingSteps,
        spreads,
        suitMeanings,
        numberMeanings,
        courtMeanings,
        helpful: ["Helpful forms", "Avoid"],
        helpfulItems: [
          "What supports progress in this situation?",
          "What choice aligns with my values?",
          "What can I learn from this conflict?",
          "What is the next wise step I can take?",
        ],
        avoidItems: [
          "Questions that control another person's choices.",
          "Yes or no questions that block reflection.",
          "Deterministic questions that remove agency.",
          "Questions asked in a state of harm or risk.",
        ],
        reversalsItems: [
          ["Working with reversals", "Decide a method. Read reversed as a block, a private process or a reduced expression. Apply the choice with consistency."],
          ["When to draw a clarifier", "Draw one clarifier only after you have worked the original card. Ask a specific question for the clarifier and stop after one."],
          ["Significators", "Select a card to represent the seeker or the theme if it helps you focus. Use intuition, rank or element."],
        ],
        exampleItems: [
          ["Question", "What supports wise choice about a new role at work?"],
          ["Spread", "Three cards for Past, Present and Future."],
          ["Cards drawn", "Past is Three of Pentacles. Present is Two of Wands. Future is The Chariot."],
        ],
        exampleText:
          "Past shows collaboration and growing skill. Present shows vision and a need to choose a direction. Future shows focused movement and commitment. Together the cards suggest that you are ready to lead a project that matches your values and strengths.",
        mythsIntro: "Many myths surround tarot. Clearing them away helps you use the deck with confidence and respect.",
        myths,
        faq: faqList,
      };

  useEffect(() => {
    if (window.location.hash) {
      requestAnimationFrame(() => scrollWithOffset(window.location.hash, false));
    }
    const onHashChange = () => scrollWithOffset(window.location.hash, true);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  return (
    <div className={`${FONT} min-h-screen text-white`} style={{ background: PAGE_BG }}>
      <Seo
        title={copy.seoTitle}
        description={copy.description}
        url={isSpanish ? `${siteUrl}/es/what-is-tarot-cards-complete-guide` : pageUrl}
        image={ogImage}
        schema={buildSchemaGraph()}
        keywords={megaKeywords}
      />

      <main className={`${WRAP} ${MAXW} py-6 sm:py-8`}>
        <article className={GUIDE_SHELL}>
          <header className="relative overflow-hidden px-4 py-8 text-center sm:px-7 sm:py-10 lg:px-8">
            <div className="mx-auto max-w-4xl">
              <h1 className="text-xl font-bold tracking-wide text-white sm:text-2xl">
                {copy.title}
              </h1>
              <div className="mt-2 flex items-center justify-center">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent" />
                <span className="px-4 text-[#f3eaff]/100" aria-hidden="true">{"\u2726"}</span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent" />
              </div>
              <p className={`${TEXT} mx-auto mt-2 max-w-3xl text-base`}>
                {copy.hero}
              </p>
            </div>

            <aside className="mx-auto mt-7 max-w-md">
              <h2 className="text-base font-semibold uppercase tracking-[0.12em] text-white/95 sm:text-lg">{copy.glance}</h2>
              <dl className="mt-4 grid grid-cols-3 gap-4">
                  {[
                    ["78", copy.labels[0]],
                    ["22", copy.labels[1]],
                    ["56", copy.labels[2]],
                  ].map(([value, label]) => (
                    <div key={label}>
                      <dt className="text-[30px] font-bold leading-none text-white">{value}</dt>
                      <dd className="mt-1 text-[12px] font-bold uppercase tracking-[0.12em] text-white/66">{label}</dd>
                    </div>
                  ))}
              </dl>
            </aside>
          </header>

          <nav aria-label={isSpanish ? "Secciones de la guía" : "Guide sections"} className="bg-[#063653]/72 px-4 py-4 sm:px-6">
            <div className="flex flex-wrap gap-2">
              {copy.quickLinks.map(([href, label]) => (
                <GuideLink key={href} href={href}>
                  {label}
                </GuideLink>
              ))}
            </div>
          </nav>

          <GuideSection id="deck-structure" title={isSpanish ? "Qué contiene el tarot" : "What Tarot Contains"} intro={copy.deckOverview}>
            <DefinitionList
              items={copy.deckItems}
            />
            <div className="mt-6 flex flex-wrap gap-2">
              {copy.deckLinks.map(([href, label]) => (
                <GuideLink key={href} href={href}>
                  {label}
                </GuideLink>
              ))}
            </div>
          </GuideSection>

          <GuideSection id="how-it-works" title={isSpanish ? "Cómo funciona una lectura" : "How a Reading Works"} intro={copy.howItWorks}>
            <StepList items={copy.readingSteps} />
          </GuideSection>

          <GuideSection
            id="spreads"
            title={isSpanish ? "Tiradas simples para principiantes" : "Simple Spreads for Beginners"}
            intro={copy.spreadsIntro}
          >
            <DefinitionList items={copy.spreads} />
          </GuideSection>

          <GuideSection id="choose" title={isSpanish ? "Elegir un mazo" : "Choosing a Deck"} intro={copy.choose}>
            <SoftList
              items={isSpanish
                ? [
                    "Elige un estilo que despierte curiosidad y uso constante.",
                    "Considera tamaño, textura y comodidad para barajar.",
                    "Una guía ayuda al comienzo, pero tus notas también importan.",
                    "Puedes comprar tu primer mazo con confianza.",
                  ]
                : [
                    "Pick a style that invites curiosity and steady use.",
                    "Consider card stock, finish and size for regular shuffling.",
                    "A guidebook can help at first, yet your notes matter most.",
                    "You may buy your first deck for yourself with confidence.",
                  ]}
            />
          </GuideSection>

          <GuideSection id="care" title={isSpanish ? "Cuidar el mazo" : "Caring for a Deck"} intro={copy.care}>
            <SoftList
              items={isSpanish
                ? [
                    "Guarda el mazo en un lugar limpio y seco.",
                    "Empieza con respiración, una intención breve o una pausa.",
                    "Usa un paño para leer y lleva un diario sencillo.",
                    "Si te ayuda, marca la intención con luz, sonido suave o naturaleza.",
                  ]
                : [
                    "Store the deck in a clean dry place and keep it together in a box or wrap.",
                    "Begin with breath, a short prayer or a clear intention for service.",
                    "Use a cloth for reading and keep a simple journal for results.",
                    "If you like, mark intention with light, gentle sound or time in nature.",
                  ]}
            />
          </GuideSection>

          <GuideSection id="elements" title={isSpanish ? "Palos y elementos" : "Suits and Elements"} intro={copy.elements}>
            <DefinitionList items={copy.suitMeanings} />
          </GuideSection>

          <GuideSection id="numerology" title={isSpanish ? "Números en el tarot" : "Numbers in Tarot"} intro={copy.numbers}>
            <CompactMeaningMap items={copy.numberMeanings} />
          </GuideSection>

          <GuideSection id="courts" title={isSpanish ? "Cartas de la corte" : "Court Cards Explained"} intro={copy.courts}>
            <CompactMeaningMap items={copy.courtMeanings} />
          </GuideSection>

          <GuideSection id="questions" title={isSpanish ? "Hacer mejores preguntas" : "Asking Better Questions"} intro={copy.questions}>
            <SplitList
              leftTitle={copy.helpful[0]}
              leftItems={copy.helpfulItems}
              rightTitle={copy.helpful[1]}
              rightItems={copy.avoidItems}
            />
          </GuideSection>

          <GuideSection id="reversals" title={isSpanish ? "Invertidas y aclaratorias" : "Reversals and Clarifiers"} intro={copy.reversals}>
            <DefinitionList
              items={copy.reversalsItems}
            />
          </GuideSection>

          <GuideSection id="example" title={isSpanish ? "Ejemplo de lectura" : "Example Reading Walkthrough"} intro={copy.example}>
            <div className="space-y-6">
              <DefinitionList items={copy.exampleItems} />
              <p className={BODY}>
                {copy.exampleText}
              </p>
            </div>
          </GuideSection>

          <GuideSection
            id="myths"
            title={isSpanish ? "Mitos comunes" : "Common Myths"}
            intro={copy.mythsIntro}
          >
            <DefinitionList items={copy.myths.map(([myth, truth]) => [myth, truth])} />
          </GuideSection>

          <GuideSection id="faq" title={isSpanish ? "Preguntas frecuentes" : "Frequently Asked Questions"}>
            <div>
              {copy.faq.map(({ q, a }, index) => (
                <details
                  key={q}
                  className={`group ${
                    index > 0
                      ? "relative before:absolute before:left-0 before:right-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-[#cbb7ea]/45 before:to-transparent"
                      : ""
                  }`}
                >
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-6 py-4 text-sm font-semibold text-white after:ml-4 after:text-[#f2d38a] after:content-['+'] sm:text-base group-open:after:content-['-']">
                    <span>{q}</span>
                  </summary>
                  <p className={`${TEXT} pb-5 text-sm leading-relaxed sm:text-base`}>{a}</p>
                </details>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {copy.deckLinks.map(([href, label]) => (
                <GuideLink key={href} href={href}>
                  {label}
                </GuideLink>
              ))}
            </div>

            <p className={`${BODY} mt-7 pt-2`}>{copy.ethics}</p>
          </GuideSection>
        </article>
      </main>
    </div>
  );
}
